import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { env } from "../config/env";
import { getNotificationDeepLink } from "../lib/notificationDeepLink";
import { notificationRealtime } from "../lib/notifications/notificationRealtime";
import { authStorage } from "../lib/storage/authStorage";
import { trackingService } from "../features/analytics/service";
import { formatISTDateTime, parseInstant } from "../utils/dateUtils";

type NotificationCategory =
    | "system"
    | "security"
    | "social"
    | "inventory"
    | "community"
    | "processing"
    | "enquiry";

interface NotificationItem {
    id: string;
    user_id: string;
    type: string;
    category: NotificationCategory | string;
    title: string;
    body: string;
    metadata: Record<string, unknown>;
    source_module: string;
    read: boolean;
    created_at: string;
    read_at?: string | null;
}

interface NotificationPreferences {
    user_id: string;
    mute_all: boolean;
    muted_categories: string[];
    email_enabled: boolean;
    email_by_category: Record<string, boolean>;
}

const apiBase = env.apiBaseUrl;
const authHeaders = () => {
    const token = authStorage.getToken();
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

const CATEGORY_OPTIONS: Array<NotificationCategory | "all"> = [
    "all",
    "system",
    "security",
    "social",
    "inventory",
    "community",
    "processing",
    "enquiry",
];

export default function NotificationsPage() {
    const navigate = useNavigate();
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [category, setCategory] = useState<NotificationCategory | "all">("all");
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
    const [savingPrefs, setSavingPrefs] = useState(false);

    const sendWs = useCallback((payload: Record<string, unknown>) => {
        notificationRealtime.send(payload);
    }, []);

    const fetchSnapshot = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("limit", "100");
            if (showUnreadOnly) params.set("unread_only", "true");
            if (category !== "all") params.set("category", category);
            const res = await fetch(`${apiBase}/api/notifications?${params.toString()}`, {
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error(`Failed loading notifications (${res.status})`);
            const data = await res.json();
            setNotifications((data.notifications ?? []) as NotificationItem[]);
            setError("");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed loading notifications");
        } finally {
            setLoading(false);
        }
    }, [category, showUnreadOnly]);

    const fetchPreferences = useCallback(async () => {
        try {
            const res = await fetch(`${apiBase}/api/notifications/preferences`, {
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error(`Failed loading preferences (${res.status})`);
            const data = await res.json();
            setPrefs(data as NotificationPreferences);
        } catch {
            // keep page usable even if prefs fails
        }
    }, []);

    useEffect(() => {
        void fetchSnapshot();
    }, [fetchSnapshot]);

    useEffect(() => {
        void fetchPreferences();
    }, [fetchPreferences]);

    useEffect(() => {
        const token = authStorage.getToken();
        if (!token) {
            setError("Login required");
            setLoading(false);
            return;
        }
        return notificationRealtime.subscribe((rawPayload) => {
            const payload = rawPayload as Record<string, unknown>;
            const type = String(payload.type ?? "");
            if (type === "notification:connected") {
                setConnected(true);
                setError("");
                return;
            }
            if (type === "notification:error") {
                setError(String(payload.message ?? "Notification error"));
                return;
            }
            if (type === "notification:snapshot") {
                const rows = (payload.notifications as NotificationItem[]) ?? [];
                setNotifications(rows);
                setLoading(false);
                setConnected(true);
                return;
            }
            if (type === "notification:new_batch") {
                const rows = (payload.notifications as NotificationItem[]) ?? [];
                if (!rows.length) return;
                setNotifications((prev) => {
                    const seen = new Set(prev.map((item) => item.id));
                    const merged = [...rows.filter((item) => !seen.has(item.id)), ...prev];
                    merged.sort(
                        (a, b) =>
                            (parseInstant(b.created_at)?.getTime() ?? 0) -
                            (parseInstant(a.created_at)?.getTime() ?? 0),
                    );
                    return merged.slice(0, 200);
                });
                return;
            }
            if (type === "notification:read:ack") {
                const notificationId = String(payload.notification_id ?? "");
                if (!notificationId) return;
                setNotifications((prev) =>
                    prev.map((n) =>
                        n.id === notificationId ? { ...n, read: true } : n,
                    ),
                );
            }
            if (type === "notification:read_all:ack") {
                setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
            }
        });
    }, []);

    const markRead = async (notificationId: string) => {
        sendWs({ type: "notification:read", notification_id: notificationId });
        setNotifications((prev) =>
            prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
        );
        try {
            await fetch(`${apiBase}/api/notifications/${notificationId}/read`, {
                method: "PATCH",
                headers: authHeaders(),
            });
        } catch {
            // no-op: ws path already handled optimistic state
        }
    };

    const markAllRead = async () => {
        sendWs({ type: "notification:read_all" });
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        try {
            await fetch(`${apiBase}/api/notifications/read-all`, {
                method: "POST",
                headers: authHeaders(),
            });
        } catch {
            // no-op
        }
    };

    const updatePrefs = async (patch: Partial<NotificationPreferences>) => {
        if (!prefs) return;
        const next = { ...prefs, ...patch };
        setPrefs(next);
        setSavingPrefs(true);
        try {
            const res = await fetch(`${apiBase}/api/notifications/preferences`, {
                method: "PATCH",
                headers: authHeaders(),
                body: JSON.stringify(patch),
            });
            if (!res.ok) throw new Error("Failed to update preferences");
            const saved = await res.json();
            setPrefs(saved as NotificationPreferences);
        } catch {
            setError("Could not save notification preferences");
            setPrefs(prefs);
        } finally {
            setSavingPrefs(false);
        }
    };

    const toggleMutedCategory = (value: string) => {
        if (!prefs) return;
        const muted = new Set(prefs.muted_categories);
        if (muted.has(value)) muted.delete(value);
        else muted.add(value);
        void updatePrefs({ muted_categories: Array.from(muted) });
    };

    const toggleEmailCategory = (value: string) => {
        if (!prefs) return;
        const next = { ...(prefs.email_by_category || {}) };
        next[value] = !Boolean(next[value]);
        void updatePrefs({ email_by_category: next });
    };

    const openNotification = async (item: NotificationItem) => {
        if (!item.read) await markRead(item.id);
        trackingService.notificationAction(item.id, "opened", item.type);
        navigate(getNotificationDeepLink(item));
    };

    const filteredNotifications = useMemo(
        () =>
            notifications.filter((n) => {
                if (showUnreadOnly && n.read) return false;
                if (category !== "all" && n.category !== category) return false;
                return true;
            }),
        [notifications, showUnreadOnly, category],
    );

    const unreadCount = notifications.filter((n) => !n.read).length;

    return (
        <div style={{ minHeight: "100vh", background: "#f7f8fb", padding: "24px 16px" }}>
            <div style={{ maxWidth: 920, margin: "0 auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: 24 }}>Notifications</h1>
                        <div style={{ marginTop: 4, color: "#6b7280", fontSize: 13 }}>
                            Realtime, non-chat updates across account, deals, and processing.
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => navigate("/chat")} style={btnSecondary}>
                            Back to AI Chat
                        </button>
                        <button onClick={markAllRead} style={btnPrimary}>
                            Mark all read ({unreadCount})
                        </button>
                    </div>
                </div>

                <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <div style={{ fontSize: 12, color: connected ? "#16a34a" : "#b45309" }}>
                        {connected ? "Realtime connected" : "Realtime reconnecting..."}
                    </div>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value as NotificationCategory | "all")}
                        style={filterSelect}
                    >
                        {CATEGORY_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <label style={{ fontSize: 13, color: "#374151" }}>
                        <input
                            type="checkbox"
                            checked={showUnreadOnly}
                            onChange={(e) => setShowUnreadOnly(e.target.checked)}
                            style={{ marginRight: 6 }}
                        />
                        Unread only
                    </label>
                </div>

                {prefs && (
                    <div style={{ ...cardStyle, marginTop: 14 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>Notification Preferences</div>
                        <div style={{ marginTop: 8, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                            <label style={{ fontSize: 13 }}>
                                <input
                                    type="checkbox"
                                    checked={prefs.mute_all}
                                    onChange={(e) => void updatePrefs({ mute_all: e.target.checked })}
                                    style={{ marginRight: 6 }}
                                    disabled={savingPrefs}
                                />
                                Mute all in-app notifications
                            </label>
                            <label style={{ fontSize: 13 }}>
                                <input
                                    type="checkbox"
                                    checked={prefs.email_enabled}
                                    onChange={(e) => void updatePrefs({ email_enabled: e.target.checked })}
                                    style={{ marginRight: 6 }}
                                    disabled={savingPrefs}
                                />
                                Enable email notification channel
                            </label>
                        </div>
                        <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
                            Mute categories (in-app):
                        </div>
                        <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {CATEGORY_OPTIONS.filter((c) => c !== "all").map((option) => {
                                const checked = prefs.muted_categories.includes(option);
                                return (
                                    <label key={`mute-${option}`} style={prefChipStyle}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleMutedCategory(option)}
                                            disabled={savingPrefs}
                                            style={{ marginRight: 6 }}
                                        />
                                        mute {option}
                                    </label>
                                );
                            })}
                        </div>
                        <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
                            Email by category:
                        </div>
                        <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {CATEGORY_OPTIONS.filter((c) => c !== "all").map((option) => {
                                const checked = Boolean(prefs.email_by_category?.[option]);
                                return (
                                    <label key={`email-${option}`} style={prefChipStyle}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleEmailCategory(option)}
                                            disabled={savingPrefs || !prefs.email_enabled}
                                            style={{ marginRight: 6 }}
                                        />
                                        email {option}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}

                {error && (
                    <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "#fef2f2", color: "#991b1b", fontSize: 13 }}>
                        {error}
                    </div>
                )}

                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                    {loading && (
                        <div style={emptyBoxStyle}>Loading notifications...</div>
                    )}
                    {!loading && filteredNotifications.length === 0 && (
                        <div style={emptyBoxStyle}>No notifications in this filter.</div>
                    )}
                    {!loading && filteredNotifications.map((item) => (
                        <div key={item.id} style={{ ...cardStyle, opacity: item.read ? 0.75 : 1 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                        <span style={{ fontWeight: 700, fontSize: 15 }}>{item.title}</span>
                                        <span style={chipStyle(item.category)}>{item.category}</span>
                                        {!item.read && <span style={unreadDotStyle}>Unread</span>}
                                    </div>
                                    <div style={{ marginTop: 6, color: "#374151", fontSize: 14 }}>
                                        {item.body}
                                    </div>
                                    <div style={{ marginTop: 7, color: "#9ca3af", fontSize: 12 }}>
                                        {formatISTDateTime(item.created_at)} · {item.source_module}
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: 8 }}>
                                    {!item.read && (
                                        <button onClick={() => markRead(item.id)} style={btnSecondary}>
                                            Mark read
                                        </button>
                                    )}
                                    <button onClick={() => void openNotification(item)} style={btnPrimary}>
                                        Open
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const btnPrimary: React.CSSProperties = {
    background: "#111827",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    cursor: "pointer",
};

const btnSecondary: React.CSSProperties = {
    background: "#fff",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    cursor: "pointer",
};

const filterSelect: React.CSSProperties = {
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 13,
    background: "#fff",
};

const cardStyle: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 14,
};

const emptyBoxStyle: React.CSSProperties = {
    background: "#fff",
    border: "1px dashed #d1d5db",
    borderRadius: 12,
    padding: 24,
    color: "#6b7280",
    textAlign: "center",
    fontSize: 14,
};

const unreadDotStyle: React.CSSProperties = {
    fontSize: 11,
    color: "#2563eb",
    border: "1px solid #bfdbfe",
    borderRadius: 999,
    padding: "2px 6px",
};

const prefChipStyle: React.CSSProperties = {
    fontSize: 12,
    border: "1px solid #e5e7eb",
    borderRadius: 999,
    padding: "4px 8px",
    background: "#f9fafb",
};

const chipStyle = (category: string): React.CSSProperties => ({
    fontSize: 11,
    background: "#f3f4f6",
    borderRadius: 999,
    padding: "2px 7px",
    textTransform: "capitalize",
    color: "#374151",
    border: "1px solid #e5e7eb",
    ...(category === "security" ? { background: "#fef2f2", color: "#991b1b" } : {}),
    ...(category === "processing" ? { background: "#ecfeff", color: "#155e75" } : {}),
    ...(category === "social" ? { background: "#faf5ff", color: "#6b21a8" } : {}),
});
